/**
 * Template picker entry for a document rendered by the backend. It has no manual
 * fields: the backend loads everything it needs from the patient's records.
 */
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { type DocumentPreviewInput, dataOrThrow, type PatientWithOwners } from '@snootsvet/shared'

import { Button } from '@/frontend/components/ui/button'
import type { DocumentEditorProps } from '@/frontend/features/printing/documents/with-document-editor'
import { openPdf } from '@/frontend/features/printing/pdf-utils'
import { useOsApi } from '@/frontend/hooks/api/use-os-api'

interface BackendDocumentConfig {
  id: string
  label: string
  request: (patient: PatientWithOwners) => DocumentPreviewInput
}

export function withBackendDocument(config: BackendDocumentConfig) {
  function Editor({ context, patientName }: DocumentEditorProps) {
    const api = useOsApi()
    const [generating, setGenerating] = useState(false)

    async function handleGenerate() {
      const { documentType, input } = config.request(context.patient)
      setGenerating(true)
      try {
        const slug = patientName.toLowerCase().replace(/\s+/g, '-')
        await openPdf(
          async () =>
            dataOrThrow(
              await api.post('/documents/:documentType/preview', {
                params: { documentType },
                body: input,
              })
            ),
          `${config.id}-${slug}.pdf`
        )
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to generate document')
      } finally {
        setGenerating(false)
      }
    }

    return (
      <>
        <p className="text-muted-foreground">
          Filled in from {patientName}'s records — ready to generate.
        </p>

        <div className="flex justify-end">
          <Button onClick={handleGenerate} disabled={generating}>
            {generating && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Generate PDF
          </Button>
        </div>
      </>
    )
  }

  return { ...config, clinic: undefined, Editor }
}
